/**
 * Vimshottari Dasha calculation using Julian Day arithmetic.
 * All five hierarchy levels: maha → antar → pratyantar → sukshma → prana.
 */

import { pymod } from "../lib/pyutil";
import type { PlainDateTime } from "../lib/datetime";
import { local_to_jd } from "./chart";
import { DASHA_SEQUENCE, DASHA_DAYS, SIDEREAL_YEAR_DAYS, NAKSHATRA_SPAN_DEG } from "./constants";
import type { DashaInfo, RawChartData } from "./models";

const _TOTAL_VIMSHOTTARI_YEARS = DASHA_SEQUENCE.reduce((acc, [, years]) => acc + years, 0); // 120

interface SublevelResult {
  lord: string;
  days_remaining: number;
  days_into: number;
  start_jd: number;
  end_jd: number;
  duration_days: number;
}

/**
 * Initialize Vimshottari Dasha using Julian Day arithmetic.
 * All calculations use days, not years/365.25.
 */
export function init_dasha_jd(natal_chart: RawChartData, birth_jd: number): Record<string, number> {
  const moon_lon = natal_chart.planets["moon"].longitude;
  // INTENTIONAL CPython-parity quirk (do not "fix"): at exact nakshatra-boundary longitudes
  // (e.g. 40.0) the division-based nak_num and the fmod-based nak_pos can disagree, giving an
  // incoherent index/position pair. Mirrors the Python source exactly and is fixture-locked;
  // same quirk in chart.get_nakshatra and helpers.get_nakshatra. See pyfloat.ts.
  const nak_num = pymod(Math.trunc(moon_lon / NAKSHATRA_SPAN_DEG), 27);
  const nak_pos = pymod(moon_lon, NAKSHATRA_SPAN_DEG) / NAKSHATRA_SPAN_DEG;

  const start_index = pymod(nak_num, 9);
  const [start_lord] = DASHA_SEQUENCE[start_index];
  const start_duration_days = DASHA_DAYS[start_lord];
  const balance_days = start_duration_days * (1.0 - nak_pos);
  const first_dasha_end_jd = birth_jd + balance_days;

  return {
    start_index: start_index,
    balance_days_at_birth: balance_days,
    first_dasha_end_jd: first_dasha_end_jd,
    birth_jd: birth_jd,
  };
}

/**
 * Generic sub-dasha calculator — works for antar, pratyantar, sukshma, and prana.
 * Each level subdivides the parent period proportionally by the Vimshottari year weights,
 * starting from the parent lord's own position in the sequence.
 * Returns: lord, days_remaining, days_into, start_jd, end_jd, duration_days.
 */
function _get_sublevel_jd(
  parent_lord: string,
  days_into_parent: number,
  parent_start_jd: number,
  parent_duration_days: number
): SublevelResult {
  const parent_index = DASHA_SEQUENCE.findIndex(([l]) => l === parent_lord);

  let cumulative_days = 0.0;
  let sub_start_jd = parent_start_jd;

  for (let i = 0; i < 9; i++) {
    const sub_index = pymod(parent_index + i, 9);
    const [sub_lord, sub_base_years] = DASHA_SEQUENCE[sub_index];
    const sub_duration_days = (sub_base_years / _TOTAL_VIMSHOTTARI_YEARS) * parent_duration_days;

    if (days_into_parent < cumulative_days + sub_duration_days) {
      const days_into_sub = days_into_parent - cumulative_days;
      const sub_end_jd = sub_start_jd + sub_duration_days;
      return {
        lord: sub_lord,
        days_remaining: sub_duration_days - days_into_sub,
        days_into: days_into_sub,
        start_jd: sub_start_jd,
        end_jd: sub_end_jd,
        duration_days: sub_duration_days,
      };
    }

    cumulative_days += sub_duration_days;
    sub_start_jd += sub_duration_days;
  }

  // Fallback — rounding edge at exact boundary
  return {
    lord: parent_lord,
    days_remaining: 0.0,
    days_into: parent_duration_days,
    start_jd: parent_start_jd,
    end_jd: parent_start_jd + parent_duration_days,
    duration_days: parent_duration_days,
  };
}

/** Locate the active mahadasha and how far into it we are. */
function _find_active_maha(
  days_elapsed: number,
  start_index: number,
  balance_days: number,
  birth_jd: number
): SublevelResult {
  if (days_elapsed < balance_days) {
    const [maha_lord] = DASHA_SEQUENCE[start_index];
    // The first mahadasha is only partially remaining at birth (its "balance").
    // Antardashas subdivide the FULL mahadasha (BPHS: antar = maha_yrs * antar_yrs / 120),
    // and birth lands partway through that full period. Model the mahadasha at its true
    // full length with a virtual start before birth, so the sub-period sequence and
    // boundaries are correct instead of being restarted/compressed into the balance.
    const full_duration = DASHA_DAYS[maha_lord];
    const elapsed_before_birth = full_duration - balance_days;
    return {
      lord: maha_lord,
      days_into: elapsed_before_birth + days_elapsed,
      start_jd: birth_jd - elapsed_before_birth,
      end_jd: birth_jd + balance_days,
      duration_days: full_duration,
      days_remaining: full_duration - (elapsed_before_birth + days_elapsed),
    };
  }

  let days_remaining = days_elapsed - balance_days;
  let current_index = pymod(start_index + 1, 9);
  let cumulative_jd = birth_jd + balance_days;

  for (;;) {
    const [lord] = DASHA_SEQUENCE[current_index];
    const duration_days = DASHA_DAYS[lord];
    if (days_remaining < duration_days) {
      return {
        lord: lord,
        days_into: days_remaining,
        start_jd: cumulative_jd,
        end_jd: cumulative_jd + duration_days,
        duration_days: duration_days,
        days_remaining: duration_days - days_remaining,
      };
    }
    days_remaining -= duration_days;
    cumulative_jd += duration_days;
    current_index = pymod(current_index + 1, 9);
  }
}

/**
 * Get the complete Vimshottari dasha chain at a given moment.
 * Populates all five levels: maha → antar → pratyantar → sukshma → prana.
 * All arithmetic is in Julian Days for maximum precision.
 *
 * NOTE: `birth_datetime` is accepted for API compatibility but unused internally —
 * all arithmetic flows from dasha_info["birth_jd"] (verified by the fixtures).
 */
export function get_current_dasha(
  current_dt: PlainDateTime,
  _birth_datetime: PlainDateTime,
  dasha_info: Record<string, number>,
  timezone_offset: number
): DashaInfo {
  const current_jd = local_to_jd(current_dt, timezone_offset);
  const birth_jd = dasha_info["birth_jd"];
  const days_elapsed = current_jd - birth_jd;

  const maha = _find_active_maha(
    days_elapsed,
    dasha_info["start_index"],
    dasha_info["balance_days_at_birth"],
    birth_jd
  );

  const antar = _get_sublevel_jd(maha.lord, maha.days_into, maha.start_jd, maha.duration_days);
  const prata = _get_sublevel_jd(antar.lord, antar.days_into, antar.start_jd, antar.end_jd - antar.start_jd);
  const suksh = _get_sublevel_jd(prata.lord, prata.days_into, prata.start_jd, prata.end_jd - prata.start_jd);
  const prana = _get_sublevel_jd(suksh.lord, suksh.days_into, suksh.start_jd, suksh.end_jd - suksh.start_jd);

  return {
    mahadasha_lord: maha.lord,
    antardasha_lord: antar.lord,
    pratyantardasha_lord: prata.lord,
    maha_days_remaining: maha.duration_days - maha.days_into,
    antar_days_remaining: antar.days_remaining,
    maha_start_jd: maha.start_jd,
    maha_end_jd: maha.end_jd,
    antar_start_jd: antar.start_jd,
    antar_end_jd: antar.end_jd,
    pratyantar_days_remaining: prata.days_remaining,
    sukshma_lord: suksh.lord,
    sukshma_days_remaining: suksh.days_remaining,
    prana_lord: prana.lord,
    prana_days_remaining: prana.days_remaining,
    pratyantar_start_jd: prata.start_jd,
    pratyantar_end_jd: prata.end_jd,
    sukshma_start_jd: suksh.start_jd,
    sukshma_end_jd: suksh.end_jd,
    prana_start_jd: prana.start_jd,
    prana_end_jd: prana.end_jd,
  };
}

/**
 * Calculate BPHS Sudarshana annual bhava (1-12) using JD precision.
 * The historical method assigns one year to each bhava in sequence from birth.
 * Function name is retained for API compatibility.
 */
export function get_profection_house(
  current_dt: PlainDateTime,
  birth_datetime: PlainDateTime,
  timezone_offset: number
): number {
  const current_jd = local_to_jd(current_dt, timezone_offset);
  const birth_jd = local_to_jd(birth_datetime, timezone_offset);
  const days_elapsed = current_jd - birth_jd;
  const years_elapsed = Math.trunc(days_elapsed / SIDEREAL_YEAR_DAYS);
  return pymod(years_elapsed, 12) + 1;
}
